import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../styles/challenge-theme-questions.css";
import Button from "../../../../ui/atoms/buttons/Button";
import InputSelect from "../../../../ui/atoms/inputs/input-select/InputSelect";
import CustomizedTextEditor from "../../../../ui/organisms/customized-text-editor/CustomizedTextEditor";
import CheckBox from "../../../../ui/atoms/checkbox/CheckBox";
import RadioButton from "../../../../ui/atoms/radio-button/RadioButton";
//
import { useSelector } from "react-redux";
import {
  capitalizeAll,
  wait,
  convertDuration,
  isEmpty,
} from "../../../../core/utils/helpers";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { onCreateChallengeThemeQuestions } from "../../services/challengeTheme";
import { ChallengeThemeQuestionsSchema } from "../../../../core/utils/validators/challenge";
import { onHandleSingleFile } from "../../../../core/utils/common-validators";
import { PiFileAudioBold } from "react-icons/pi";
import { SlClose, SlPicture } from "react-icons/sl";
//
import Swal from "sweetalert2";
import useAxiosPrivate from "../../../../app/providers/context-provider/useAxiosPrivate";

const ChallengeThemeQuestionsCreation = () => {
  // const { id, title_en, title_fr } = useParams();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();

  const [isSending, setIsSending] = useState(false);

  const [points, setPoints] = useState(10);
  const [timing, setTiming] = useState(10);
  const [randomAnswerOrder, setRandomAnswerOrder] = useState(false);

  const [questionDescriptionEn, setQuestionDescriptionEn] = useState("");
  const [questionExplanationEn, setQuestionExplanationEn] = useState("");
  const [questionAnswerEn, setQuestionAnswerEn] = useState("");

  const [questionAnswersEn, setQuestionAnswersEn] = useState([]);
  const [questionAnswersFr, setQuestionAnswersFr] = useState([]);

  const [audioFileEn, setAudioFileEn] = useState(null);
  const [audioPreviewUrlEn, setAudioPreviewUrlEn] = useState(null);
  const [imageFileEn, setImageFileEn] = useState(null);
  const [imagePreviewUrlEn, setImagePreviewUrlEn] = useState(null);

  const [questionDescriptionFr, setQuestionDescriptionFr] = useState("");
  const [questionExplanationFr, setQuestionExplanationFr] = useState("");
  const [questionAnswerFr, setQuestionAnswerFr] = useState("");

  const [audioFileFr, setAudioFileFr] = useState(null);
  const [audioPreviewUrlFr, setAudioPreviewUrlFr] = useState(null);
  const [imageFileFr, setImageFileFr] = useState(null);
  const [imagePreviewUrlFr, setImagePreviewUrlFr] = useState(null);

  const currentChallengeTheme = useSelector(
    (state) => state?.challengeState?.currentChallengeTheme,
  );

  const _current_challenge_theme_id = currentChallengeTheme?.id;
  const _current_challenge_theme_title_en = currentChallengeTheme?.title_en;
  const _current_challenge_theme_title_fr = currentChallengeTheme?.title_fr;

  const {
    watch,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(ChallengeThemeQuestionsSchema),
  });

  const questionType = watch("type");

  useEffect(() => {
    // Optionally update all `is_correct` to true when selection changes
    if (!isEmpty(questionAnswersEn)) {
      const updatedData = questionAnswersEn.map((item) => ({
        ...item,
        is_correct: false,
      }));
      setQuestionAnswersEn(updatedData);
    }
  }, [questionType]);

  useEffect(() => {
    return () => {
      if (audioPreviewUrlEn) {
        URL.revokeObjectURL(audioPreviewUrlEn);
      }
    };
  }, [audioPreviewUrlEn]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrlEn) {
        URL.revokeObjectURL(imagePreviewUrlEn);
      }
    };
  }, [imagePreviewUrlEn]);

  useEffect(() => {
    return () => {
      if (audioPreviewUrlFr) {
        URL.revokeObjectURL(audioPreviewUrlFr);
      }
    };
  }, [audioPreviewUrlFr]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrlFr) {
        URL.revokeObjectURL(imagePreviewUrlFr);
      }
    };
  }, [imagePreviewUrlFr]);

  const removeFile = (type, lang) => {
    if (type === "audio" && lang === "en") {
      setAudioFileEn(null);
      setAudioPreviewUrlEn(null);
    }
    if (type === "audio" && lang === "fr") {
      setAudioFileFr(null);
      setAudioPreviewUrlFr(null);
    }
    if (type === "image" && lang === "en") {
      setImageFileEn(null);
      setImagePreviewUrlEn(null);
    }
    if (type === "image" && lang === "fr") {
      setImageFileFr(null);
      setImagePreviewUrlFr(null);
    }
  };

  const onAddAnswer = (lang) => {
    if (lang === "en") {
      const _questionAnswerEn = questionAnswerEn.trim();
      if (_questionAnswerEn === "") {
        Swal.fire({
          title: "[ENGLISH] Empty Answer Field",
          text: "Please enter an answer before push it.",
          icon: "warning",
        });
        return;
      }
      // Check if the answer already exists (case-insensitive)
      const alreadyExists = questionAnswersEn.some(
        (ans) =>
          ans.answer.trim().toLowerCase() ===
          questionAnswerEn.trim().toLowerCase(),
      );
      if (alreadyExists) {
        Swal.fire({
          title: "[ENGLISH] Duplicate Answer",
          text: "This answer already exists. Please enter a different one.",
          icon: "error",
        });
        return;
      }
      // Add new answer
      setQuestionAnswersEn((prevState) => [
        ...prevState,
        {
          is_correct: false,
          answer: questionAnswerEn,
        },
      ]);
      // Clear input
      setQuestionAnswerEn("");
    }
    if (lang === "fr") {
      const _questionAnswerFr = questionAnswerFr.trim();
      if (isEmpty(_questionAnswerFr)) {
        Swal.fire({
          title: "[FRENCH] Empty Answer Field",
          text: "Please enter an answer before push it.",
          icon: "warning",
        });
        return;
      }
      // Check if the answer already exists (case-insensitive)
      const alreadyExists = questionAnswersFr.some(
        (ans) =>
          ans.answer.trim().toLowerCase() ===
          questionAnswerFr.trim().toLowerCase(),
      );
      if (alreadyExists) {
        Swal.fire({
          title: "[FRENCH] Duplicate Answer",
          text: "This answer already exists. Please enter a different one.",
          icon: "error",
        });
        return;
      }
      // Add new answer
      setQuestionAnswersFr((prevState) => [
        ...prevState,
        {
          is_correct: false,
          answer: questionAnswerFr,
        },
      ]);
      // Clear input
      setQuestionAnswerFr("");
    }
  };

  const onHandleRightAnswers = (idx, lang) => {
    if (lang === "en") {
      setQuestionAnswersEn((prev) =>
        questionType === "OCA"
          ? prev.map((item, i) => ({
              ...item,
              is_correct: i === idx,
            }))
          : prev.map((item, i) =>
              i === idx ? { ...item, is_correct: !item.is_correct } : item,
            ),
      );
    }
    if (lang === "fr") {
      setQuestionAnswersFr((prev) =>
        questionType === "OCA"
          ? prev.map((item, i) => ({
              ...item,
              is_correct: i === idx,
            }))
          : prev.map((item, i) =>
              i === idx ? { ...item, is_correct: !item.is_correct } : item,
            ),
      );
    }
  };

  const onRemoveAnswer = (idx, lang) => {
    if (lang === "en") {
      setQuestionAnswersEn((prev) => prev.filter((_, i) => i !== idx));
    }
    if (lang === "fr") {
      setQuestionAnswersFr((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  const onCheckEmptyFields = () => {
    const fields = [
      {
        value: questionDescriptionEn,
        message:
          "[ENGLISH] Please provide the description's content of question.",
      },
      {
        value: questionDescriptionFr,
        message: "[FRENCH] Please provide the description of question.",
      },
      {
        value: questionExplanationEn,
        message:
          "[ENGLISH] Please provide the explanation's content of question.",
      },
      {
        value: questionExplanationFr,
        message:
          "[FRENCH] Please provide the explanation's content of question.",
      },
    ];

    for (const field of fields) {
      if (isEmpty(field.value)) {
        Swal.fire({
          title: "Question Composition",
          text: field.message,
          icon: "warning",
        });
        return false;
      }
    }
    if (!isEmpty(questionAnswersEn)) {
      let counter = 0;
      for (const row of questionAnswersEn) {
        counter = row.is_correct ? counter + 1 : counter;
      }
      if (counter === 0) {
        Swal.fire({
          title: "[ENGLISH] Question Composition Answers",
          text: `You should choose ${
            questionType === "OCA" ? "one" : "at least one"
          } right answer among answers!`,
          icon: "warning",
        });
        return false;
      }
    }
    if (!isEmpty(questionAnswersFr)) {
      let counter = 0;
      for (const row of questionAnswersFr) {
        counter = row.is_correct ? counter + 1 : counter;
      }
      if (counter === 0) {
        Swal.fire({
          title: "[FRENCH] Question Composition Answers",
          text: `You should choose ${
            questionType === "OCA" ? "one" : "at least one"
          } right answer among answers!`,
          icon: "warning",
        });
        return false;
      }
    }
    //
    return true;
  };

  const onReInitializeFields = () => {
    setPoints(10);
    setTiming(10);
    setRandomAnswerOrder(false);
    setAudioFileEn(null);
    setAudioPreviewUrlEn(null);
    setImageFileEn(null);
    setImagePreviewUrlEn(null);
    setQuestionDescriptionEn("");
    setQuestionDescriptionFr("");
    setQuestionExplanationEn("");
    setQuestionExplanationFr("");
    //
    setQuestionAnswersEn([]);
    setQuestionAnswersFr([]);
  };

  const onSubmit = async (data) => {
    const isResult = onCheckEmptyFields();
    if (!isResult) return;
    //
    setIsSending(true);
    Swal.fire({
      title: "Creating new Challenge Theme Question...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    await wait(700);
    //
    let formData = new FormData();
    //
    formData.append("challenge_theme_id", _current_challenge_theme_id);
    formData.append("types", data?.type);
    formData.append("difficulty", data?.difficulty);
    formData.append("points", points);
    formData.append("timing", timing);
    formData.append(
      "random_answer_order",
      randomAnswerOrder ? "true" : "false",
    );

    if (audioFileEn) {
      formData.append("audio_file_en", audioFileEn);
    }

    if (audioFileFr) {
      formData.append("audio_file_fr", audioFileFr);
    }

    if (imageFileEn) {
      formData.append("image_file_en", imageFileEn);
    }

    if (imageFileFr) {
      formData.append("image_file_fr", imageFileFr);
    }

    formData.append("question_description_en", questionDescriptionEn);
    formData.append("question_description_fr", questionDescriptionFr);
    formData.append("question_explanation_en", questionExplanationEn);
    formData.append("question_explanation_fr", questionExplanationFr);

    formData.append("question_answers_en", JSON.stringify(questionAnswersEn));
    formData.append("question_answers_fr", JSON.stringify(questionAnswersFr));
    //
    onCreateChallengeThemeQuestions(axiosPrivate, formData)
      .then((response) => {
        const _data = response?.data;
        Swal.close();
        setIsSending(false);
        //
        if (_data?.status) {
          Swal.fire({
            title: "Challenge Theme Questions Creation",
            text: _data?.message,
            icon: "success",
            confirmButtonText: "OK",
            didOpen: () => {
              const btn = Swal.getConfirmButton(); //
              btn.style.backgroundColor = "#2840a8";
              btn.style.color = "white";
            },
          });
          reset();
          onReInitializeFields();
        } else {
          Swal.fire({
            title: "Challenge Theme Questions Creation",
            text: _data?.message,
            icon: "info",
          });
        }
      })
      .catch((error) => {
        setIsSending(false);
        Swal.close();
        if (!error?.response) {
          Swal.fire({
            title: "Challenge Theme Questions Creation",
            text: "No server response",
            icon: "warning",
          });
        } else {
          Swal.fire({
            title: "Challenge Theme Questions Creation",
            text: error?.response?.data?.detail?.message,
            icon: "error",
          });
        }
      });
  };

  return (
    <form
      className="challenge-theme-questions"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="challenge-theme-questions-head">
        <h1>⁉️ Challenge Theme Questions Creation</h1>
        <div className="w-full mb-xs">
          <span>Challenge Theme :</span>
          <h3>
            {capitalizeAll(_current_challenge_theme_title_en)} /{" "}
            {capitalizeAll(_current_challenge_theme_title_fr)}
          </h3>
        </div>
        <div className="w-full d-flex justify-space-between align-center">
          <Button
            type={"button"}
            preferedStyle={"default"}
            title={"⬅️ Back to ..."}
            action={() => navigate(-1)}
          />
          <Button
            type={isSending ? "button" : "submit"}
            preferedStyle={isSending ? "default" : "normal"}
            title={
              isSending
                ? "Creating new Challenge Theme Questions..."
                : "Validate and Create new Challenge Theme Questions"
            }
          />
        </div>
      </div>
      <div className="challenge-theme-questions-body">
        <div className="w-50">
          <InputSelect
            name={"type"}
            error={errors?.type?.message}
            register={register}
            data={[
              { key: "OCA", title: "One Choice Answer", is_activated: false },
              {
                key: "MCA",
                title: "Multi Choices Answer",
                is_activated: false,
              },
            ]}
            placeholder={"Question Type(Answer's Type)"}
          />
        </div>
        <div className="w-50">
          <InputSelect
            name={"difficulty"}
            error={errors?.difficulty?.message}
            register={register}
            data={[
              { key: "beginner", title: "Beginner Level", is_activated: false },
              {
                key: "intermediate",
                title: "Intermidiate Level",
                is_activated: false,
              },
              { key: "advanced", title: "Advanced Level", is_activated: false },
            ]}
            placeholder={"Question Difficulty(Level)"}
          />
        </div>
        <div className="w-full d-flex gap-md mt-md">
          <div className="w-40 timing-wrapper">
            <span>Question Total Points</span>
            <div className="w-full d-flex align-center gap-xs">
              <input
                className="w-60"
                type="range"
                name="points"
                id="points"
                min={10}
                max={30}
                step={1}
                value={points}
                onChange={(e) => setPoints(e.target.value)}
              />
              <output htmlFor="timing">{points} Points</output>
            </div>
          </div>
          <div className="w-40 timing-wrapper">
            <span>Question Timing</span>
            <div className="w-full d-flex align-center gap-xs">
              <input
                className="w-60"
                type="range"
                name="timing"
                id="timing"
                min={10}
                max={1800}
                step={1}
                value={timing}
                onChange={(e) => setTiming(e.target.value)}
              />
              <output htmlFor="timing">{convertDuration(timing)}</output>
            </div>
          </div>
          <div className="w-20 d-flex align-center gap-md">
            <span>Random Order of Answers ?</span>
            <CheckBox
              id={"random-answer-order"}
              checked={randomAnswerOrder}
              onChange={(e) => setRandomAnswerOrder(e?.target?.checked)}
            />
          </div>
        </div>
        <div className="w-full d-flex gap-md mt-md">
          <div className="w-50 mb-lg">
            <h3>English Version</h3>
            {/* Audio process */}
            <div className="w-full mb-lg">
              <div className="browse-image">
                <input
                  type="file"
                  id="audio_input"
                  accept="audio/mpeg,audio/wav,audio/mp3"
                  onChange={(e) => {
                    const _audioFile = onHandleSingleFile(e);
                    if (!_audioFile || !_audioFile.type.startsWith("audio/"))
                      return;

                    setAudioFileEn(_audioFile);
                    //   Create a temporary URL for the selected fil
                    const newPreviewUrl = URL.createObjectURL(_audioFile);
                    setAudioPreviewUrlEn(newPreviewUrl);
                  }}
                />
                <label htmlFor="audio_input">
                  <PiFileAudioBold className="icon" />
                  <span>Load Audio</span>
                </label>
              </div>
              {audioPreviewUrlEn && (
                <div className="files-container">
                  <div className="file-item file-item-audio">
                    <span className="file-icon">
                      <PiFileAudioBold className="icon" />
                    </span>
                    <div className="file-description">
                      <audio
                        className="w-full"
                        controls
                        controlsList="nodownload"
                        onContextMenu={(e) => e.preventDefault()}
                      >
                        <source
                          src={audioPreviewUrlEn}
                          type={audioFileEn?.type}
                        />
                      </audio>
                    </div>
                    <span
                      className="file-action"
                      onClick={() => removeFile("audio", "en")}
                    >
                      <SlClose className="icon" />
                    </span>
                  </div>
                </div>
              )}
            </div>
            {/* Image process */}
            <div className="w-full mb-lg">
              <div className="browse-image">
                <input
                  type="file"
                  id="image_input"
                  accept="image/*"
                  onChange={(e) => {
                    const _imageFile = onHandleSingleFile(e);
                    if (!_imageFile || !_imageFile.type.startsWith("image/"))
                      return;

                    setImageFileEn(_imageFile);
                    //   Create a temporary URL for the selected fil
                    const newPreviewUrl = URL.createObjectURL(_imageFile);
                    setImagePreviewUrlEn(newPreviewUrl);
                  }}
                />
                <label htmlFor="image_input">
                  <SlPicture className="icon" />
                  <span>Load Illustrative Image</span>
                </label>
              </div>
              {imagePreviewUrlEn && (
                <div className="files-container">
                  <div className="file-item file-item-image">
                    <div className="file-description">
                      <img
                        src={imagePreviewUrlEn}
                        alt="cover illustration Preview"
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>
                    <span
                      className="file-action"
                      onClick={() => removeFile("image", "en")}
                    >
                      <SlClose className="icon" />
                    </span>
                  </div>
                </div>
              )}
            </div>
            {/*  */}
            <div className="w-full content-wrapper">
              <span>Question Description</span>
              <hr />
              <CustomizedTextEditor
                content={questionDescriptionEn}
                setContent={setQuestionDescriptionEn}
              />
            </div>
            <div className="w-full content-wrapper">
              <span>Explanations Description</span>
              <hr />
              <CustomizedTextEditor
                content={questionExplanationEn}
                setContent={setQuestionExplanationEn}
              />
            </div>
            <div className="w-full content-wrapper">
              <span>Answers</span>
              <hr />
              <div className="answers-wrapper">
                {isEmpty(questionAnswersEn) ? (
                  <p
                    style={{
                      color: "brown",
                      fontSize: "1rem",
                      fontStyle: "italic",
                    }}
                  >
                    Waiting for answers to be setup!
                  </p>
                ) : (
                  questionAnswersEn?.map((row, idx) => (
                    <div className="answer-item" key={idx}>
                      {!isEmpty(questionType) && (
                        <div className="w-full d-flex justify-space-between align-center">
                          {questionType === "OCA" ? (
                            <RadioButton
                              id={`answer-option-${idx}`}
                              name={`answer-option-${idx}`}
                              title={"is the Right One ?"}
                              checked={row.is_correct}
                              onChange={() => onHandleRightAnswers(idx, "en")}
                            />
                          ) : (
                            <CheckBox
                              id={`answer-option-${idx}`}
                              title={"is the Right One ?"}
                              checked={row.is_correct}
                              onChange={() => onHandleRightAnswers(idx, "en")}
                            />
                          )}
                          <SlClose
                            className="icon"
                            onClick={() => onRemoveAnswer(idx, "en")}
                          />
                        </div>
                      )}
                      <p
                        dangerouslySetInnerHTML={{
                          __html: row?.answer,
                        }}
                      />
                    </div>
                  ))
                )}
              </div>
              <CustomizedTextEditor
                content={questionAnswerEn}
                setContent={setQuestionAnswerEn}
              />
              <div className="w-full d-flex justify-center align-center mt-md">
                <Button
                  type={"button"}
                  preferedStyle={"default"}
                  title={"Add answer"}
                  action={() => onAddAnswer("en")}
                />
              </div>
            </div>
          </div>
          <div className="w-50 mb-lg">
            <h3>French Version</h3>
            {/* Audio process */}
            <div className="w-full mb-lg">
              <div className="browse-image">
                <input
                  type="file"
                  id="fr_audio_input"
                  accept="audio/mpeg,audio/wav,audio/mp3"
                  onChange={(e) => {
                    const _audioFile = onHandleSingleFile(e);
                    if (!_audioFile || !_audioFile.type.startsWith("audio/"))
                      return;

                    setAudioFileFr(_audioFile);
                    //   Create a temporary URL for the selected fil
                    const newPreviewUrl = URL.createObjectURL(_audioFile);
                    setAudioPreviewUrlFr(newPreviewUrl);
                  }}
                />
                <label htmlFor="fr_audio_input">
                  <PiFileAudioBold className="icon" />
                  <span>Load Audio</span>
                </label>
              </div>
              {audioPreviewUrlFr && (
                <div className="files-container">
                  <div className="file-item file-item-audio">
                    <span className="file-icon">
                      <PiFileAudioBold className="icon" />
                    </span>
                    <div className="file-description">
                      <audio
                        className="w-full"
                        controls
                        controlsList="nodownload"
                        onContextMenu={(e) => e.preventDefault()}
                      >
                        <source
                          src={audioPreviewUrlFr}
                          type={audioFileFr?.type}
                        />
                      </audio>
                    </div>
                    <span
                      className="file-action"
                      onClick={() => removeFile("audio", "fr")}
                    >
                      <SlClose className="icon" />
                    </span>
                  </div>
                </div>
              )}
            </div>
            {/* Image process */}
            <div className="w-full mb-lg">
              <div className="browse-image">
                <input
                  type="file"
                  id="fr_image_input"
                  accept="image/*"
                  onChange={(e) => {
                    const _imageFile = onHandleSingleFile(e);
                    if (!_imageFile || !_imageFile.type.startsWith("image/"))
                      return;

                    setImageFileFr(_imageFile);
                    //   Create a temporary URL for the selected fil
                    const newPreviewUrl = URL.createObjectURL(_imageFile);
                    setImagePreviewUrlFr(newPreviewUrl);
                  }}
                />
                <label htmlFor="fr_image_input">
                  <SlPicture className="icon" />
                  <span>Load Illustrative Image</span>
                </label>
              </div>
              {imagePreviewUrlFr && (
                <div className="files-container">
                  <div className="file-item file-item-image">
                    <div className="file-description">
                      <img
                        src={imagePreviewUrlFr}
                        alt="french cover illustration Preview"
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>
                    <span
                      className="file-action"
                      onClick={() => removeFile("image", "fr")}
                    >
                      <SlClose className="icon" />
                    </span>
                  </div>
                </div>
              )}
            </div>
            {/*  */}
            <div className="w-full content-wrapper">
              <span>Question Description</span>
              <hr />
              <CustomizedTextEditor
                content={questionDescriptionFr}
                setContent={setQuestionDescriptionFr}
              />
            </div>
            <div className="w-full content-wrapper">
              <span>Explanations Description</span>
              <hr />
              <CustomizedTextEditor
                content={questionExplanationFr}
                setContent={setQuestionExplanationFr}
              />
            </div>
            <div className="w-full content-wrapper">
              <span>Answers</span>
              <hr />
              <div className="answers-wrapper">
                {isEmpty(questionAnswersFr) ? (
                  <p
                    style={{
                      color: "brown",
                      fontSize: "1rem",
                      fontStyle: "italic",
                    }}
                  >
                    Waiting for answers to be setup!
                  </p>
                ) : (
                  questionAnswersFr?.map((row, idx) => (
                    <div className="answer-item" key={idx}>
                      {!isEmpty(questionType) && (
                        <div className="w-full d-flex justify-space-between align-center">
                          {questionType === "OCA" ? (
                            <RadioButton
                              id={`fr-answer-option-${idx}`}
                              name={`fr-answer-option-${idx}`}
                              title={"is the Right One ?"}
                              checked={row.is_correct}
                              onChange={() => onHandleRightAnswers(idx, "fr")}
                            />
                          ) : (
                            <CheckBox
                              id={`fr-answer-option-${idx}`}
                              title={"is the Right One ?"}
                              checked={row.is_correct}
                              onChange={() => onHandleRightAnswers(idx, "fr")}
                            />
                          )}
                          <SlClose
                            className="icon"
                            onClick={() => onRemoveAnswer(idx, "fr")}
                          />
                        </div>
                      )}
                      <p
                        dangerouslySetInnerHTML={{
                          __html: row?.answer,
                        }}
                      />
                    </div>
                  ))
                )}
              </div>
              <CustomizedTextEditor
                content={questionAnswerFr}
                setContent={setQuestionAnswerFr}
              />
              <div className="w-full d-flex justify-center align-center mt-md">
                <Button
                  type={"button"}
                  preferedStyle={"default"}
                  title={"Add answer"}
                  action={() => onAddAnswer("fr")}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ChallengeThemeQuestionsCreation;
